/**
 * InviteDialog — phone + name + role form for issuing a team invite,
 * with a success view that shows the join link + copy + WhatsApp deep
 * link.
 *
 * Role picker (ADR-0030): the role options come from `roles.list` — the
 * same workspace roles the Settings ▸ Roles & org editor manages and the
 * ChangeRoleDialog assigns. Before this the dialog hard-coded the two
 * legacy bands (Founder/Operator), so a newly-invited member could never
 * land on a custom role; the invite carries the chosen `roleId` and
 * `acceptInvite` stamps it onto the new membership.
 *
 * Why the link is surfaced even though the API auto-sends a WhatsApp
 * template: in dev the adapter is in stub mode (no Meta credentials);
 * in prod the send can be held by a guardrail (recent-conversation,
 * rate-limit). The inviter gets a deterministic fallback either way —
 * copy the link and send it themselves.
 */
import * as React from "react";
import { Check, Copy, MessageCircle } from "lucide-react";
import {
  Alert,
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@flint/ui";
import { team as contract } from "@flint/api-contract";
import { trpc } from "../../../lib/trpc.ts";

interface InviteSuccess {
  inviteId: string;
  joinUrl: string;
  phone: string;
  roleName: string;
  expiresAt: string;
}

export interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after the mutation succeeds — caller refetches the list. */
  onInvited: (result: { joinUrl: string; phone: string; roleName: string }) => void;
}

export function InviteDialog({ open, onOpenChange, onInvited }: InviteDialogProps) {
  const [phone, setPhone] = React.useState("");
  const [name, setName] = React.useState("");
  const [roleId, setRoleId] = React.useState<string>("");
  const [clientError, setClientError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<InviteSuccess | null>(null);

  const roles = trpc.roles.list.useQuery({}, { enabled: open });
  const roleList = roles.data?.roles ?? [];
  const selectedRole = roleList.find((r) => r.id === roleId) ?? null;

  React.useEffect(() => {
    if (open) {
      setPhone("");
      setName("");
      setRoleId("");
      setClientError(null);
      setSuccess(null);
    }
  }, [open]);

  const invite = trpc.team.invite.useMutation({
    onSuccess: (result) => {
      const next: InviteSuccess = {
        inviteId: result.inviteId,
        joinUrl: result.joinUrl,
        phone: result.phone,
        roleName: result.roleName,
        expiresAt: result.expiresAt,
      };
      setSuccess(next);
      // Refresh the team list immediately so the new pending invite shows
      // up behind the dialog while the inviter is still on the success view.
      onInvited({ joinUrl: next.joinUrl, phone: next.phone, roleName: next.roleName });
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    const trimmedPhone = phone.replace(/\D/g, "");
    const payload = {
      phone: trimmedPhone,
      roleId,
      ...(name.trim() ? { name: name.trim() } : {}),
    };
    const parsed = contract.inviteInput.safeParse(payload);
    if (!parsed.success) {
      setClientError(parsed.error.issues[0]?.message ?? "Check the values you entered.");
      return;
    }
    invite.mutate(parsed.data);
  };

  const wireError = invite.error?.message ?? null;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {success ? (
          <InviteSuccessView
            success={success}
            displayName={name.trim() || null}
            onClose={() => onOpenChange(false)}
          />
        ) : (
          <form onSubmit={submit} noValidate>
            <DialogHeader>
              <DialogTitle>Invite a teammate</DialogTitle>
              <DialogDescription>
                They&apos;ll get a WhatsApp message with a link to join. The link is good for 30
                minutes — resend to issue a fresh one.
              </DialogDescription>
            </DialogHeader>

            <div className="py-4 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-phone">Phone (Indian mobile)</Label>
                <Input
                  id="invite-phone"
                  autoFocus
                  type="tel"
                  autoComplete="tel"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="9876543210"
                  className="font-mono"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  disabled={invite.isPending}
                />
                <p className="text-xs text-muted">10 digits, no country code.</p>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-name">Display name (optional)</Label>
                <Input
                  id="invite-name"
                  value={name}
                  maxLength={120}
                  placeholder="e.g. Priya Mehta"
                  onChange={(e) => setName(e.target.value)}
                  disabled={invite.isPending}
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="invite-role">Role</Label>
                {roles.isLoading ? (
                  <p className="text-sm text-muted">Loading roles…</p>
                ) : roles.error ? (
                  <Alert
                    intent="bad"
                    title="Couldn't load roles"
                    description={roles.error.message}
                  />
                ) : roleList.length === 0 ? (
                  <Alert
                    intent="warn"
                    title="No roles defined"
                    description="Create roles in Settings ▸ Roles & org before inviting teammates."
                  />
                ) : (
                  <>
                    <Select
                      value={roleId}
                      onValueChange={setRoleId}
                      disabled={invite.isPending}
                    >
                      <SelectTrigger id="invite-role">
                        <SelectValue placeholder="Select a role…" />
                      </SelectTrigger>
                      <SelectContent className="max-w-[--radix-select-trigger-width]">
                        {roleList.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            {r.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedRole?.description ? (
                      <p className="text-xs text-muted leading-relaxed">
                        {selectedRole.description}
                      </p>
                    ) : null}
                  </>
                )}
              </div>

              {clientError ? <p className="text-xs text-bad">{clientError}</p> : null}
              {wireError && !clientError ? (
                <Alert intent="bad" title="Couldn't send invite" description={wireError} />
              ) : null}
            </div>

            <DialogFooter>
              <Button
                type="button"
                intent="ghost"
                onClick={() => onOpenChange(false)}
                disabled={invite.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={invite.isPending || phone.length !== 10 || !roleId}>
                {invite.isPending ? "Sending…" : "Send invite"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </DialogRoot>
  );
}

interface InviteSuccessViewProps {
  success: InviteSuccess;
  displayName: string | null;
  onClose: () => void;
}

function InviteSuccessView({ success, displayName, onClose }: InviteSuccessViewProps) {
  const [copied, setCopied] = React.useState(false);
  const message = buildWhatsappMessage({
    joinUrl: success.joinUrl,
    inviteeName: displayName,
    roleName: success.roleName,
  });
  const waHref = `https://wa.me/91${success.phone}?text=${encodeURIComponent(message)}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(success.joinUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* swallow — fall back to the readonly input the user can select manually */
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Invite sent</DialogTitle>
        <DialogDescription>
          We&apos;ve queued a WhatsApp message to +91 {formatPhone(success.phone)}. If they
          don&apos;t receive it within a minute, copy the link below and send it manually.
        </DialogDescription>
      </DialogHeader>

      <div className="py-4 flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="invite-success-url">Join link</Label>
          <div className="flex gap-2">
            <Input
              id="invite-success-url"
              readOnly
              value={success.joinUrl}
              className="font-mono text-xs"
              onFocus={(e) => e.currentTarget.select()}
            />
            <Button type="button" intent="ghost" size="md" onClick={copy} className="shrink-0">
              {copied ? (
                <>
                  <Check size={14} strokeWidth={2} aria-hidden />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={14} strokeWidth={2} aria-hidden />
                  Copy
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted">Expires {formatExpiresAt(success.expiresAt)}.</p>
        </div>

        <a
          href={waHref}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex h-10 items-center justify-center gap-2 px-4 rounded-sm text-sm font-medium bg-ok text-paper hover:opacity-90 transition-opacity"
        >
          <MessageCircle size={15} strokeWidth={2} aria-hidden />
          Open WhatsApp to +91 {formatPhone(success.phone)}
        </a>
      </div>

      <DialogFooter>
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </DialogFooter>
    </>
  );
}

function buildWhatsappMessage(opts: {
  joinUrl: string;
  inviteeName: string | null;
  roleName: string;
}): string {
  const greeting = opts.inviteeName ? `Hi ${opts.inviteeName},` : "Hi,";
  return [
    greeting,
    "",
    `You've been invited to join our Flint workspace as ${opts.roleName}.`,
    "",
    "Tap the link below to set up your access (valid for 30 minutes):",
    opts.joinUrl,
  ].join("\n");
}

function formatPhone(digits: string): string {
  // Render as `98765 43210` so the trailing digits are easy to eyeball.
  if (digits.length !== 10) return digits;
  return `${digits.slice(0, 5)} ${digits.slice(5)}`;
}

function formatExpiresAt(iso: string): string {
  const date = new Date(iso);
  const diffMin = Math.max(0, Math.round((date.getTime() - Date.now()) / 60_000));
  if (diffMin <= 0) return "now";
  if (diffMin < 60) return `in ${diffMin} min`;
  const hrs = Math.round(diffMin / 60);
  return `in ~${hrs} hr`;
}
